<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── LOADING ── -->
      <div v-if="isLoading" class="loading-row">
        <div class="loading-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="loading-text">Gathering the chapter…</p>
      </div>

      <!-- ── CHAPTER CONTENT ── -->
      <template v-else>

        <!-- ── BACK ── -->
        <button class="back-btn" @click="$router.push('/groups')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="15 18 9 12 15 6"/></svg>
          <span>All Chapters</span>
        </button>

        <!-- ── HERO ── -->
        <div class="hero">
          <div class="hero-img-wrap">
            <img
              v-if="chapterDoc && chapterDoc.imageURL"
              :src="chapterDoc.imageURL"
              :alt="city"
              class="hero-img"
            />
            <div v-else class="hero-img-placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
            </div>
            <div class="hero-overlay"></div>
          </div>

          <div class="hero-body">
            <div class="section-hd">
              <div class="section-rule l"></div>
              <span class="section-eyebrow">City Chapter</span>
              <div class="section-rule"></div>
            </div>
            <h1 class="hero-title">{{ city }}</h1>
            <p class="hero-desc">
              {{ (chapterDoc && chapterDoc.description) || "The gathering point for every member who's recommended a place here, and everyone about to land." }}
            </p>

            <div class="hero-meta">
              <div class="meta-chip">
                <span class="meta-num">{{ contributorCount }}</span>
                <span class="meta-label">Contributors</span>
              </div>
              <div class="meta-divider"></div>
              <div class="meta-chip">
                <span class="meta-num">{{ recommendationPosts.length }}</span>
                <span class="meta-label">Recommendations</span>
              </div>
              <div class="meta-divider"></div>
              <div class="meta-chip">
                <span class="meta-num">{{ travelPlanPosts.length }}</span>
                <span class="meta-label">Traveling Now</span>
              </div>
              <div class="meta-divider"></div>
              <div class="meta-chip">
                <span class="meta-num">{{ arrangementTotal }}</span>
                <span class="meta-label">Arrangements Requested</span>
              </div>
            </div>

            <div class="hero-actions">
              <button class="join-btn" :class="{ joined: isMember }" @click="toggleMembership">
                <div class="btn-tick"></div>
                <span>{{ isMember ? 'Leave Chapter' : 'Join Chapter' }}</span>
                <div class="btn-tick"></div>
              </button>
              <button class="edit-details-btn" @click="startEditChapter">
                {{ chapterDoc && (chapterDoc.description || chapterDoc.imageURL) ? 'Edit Chapter Details' : 'Add a Tagline & Photo' }}
              </button>
              <button v-if="hasEditHistory" class="view-history-btn" @click="openEditHistory">
                Edited · View History
              </button>
            </div>
          </div>
        </div>

        <!-- ── TOP RECOMMENDATIONS ── -->
        <div class="section-hd" style="margin-top: 64px;">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">From Explore</span>
          <div class="section-rule"></div>
        </div>
        <div class="posts-header">
          <h2 class="section-title" style="margin-bottom: 0;">Top <em>Recommendations</em></h2>
          <div style="display: flex; gap: 12px; align-items: center;">
            <button class="new-post-btn" @click="openCreateExploreDialog('recommendation')">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              <span>Recommend a Place</span>
            </button>
            <button class="new-post-btn" @click="openExploreForCity">
              <span>See All in Explore</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="width:14px;height:14px;"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
          </div>
        </div>

        <div v-if="topCityPosts.length" class="place-results">
          <ExplorePostCard
            v-for="post in topCityPosts"
            :key="post.id"
            :post="post"
            :currentUserUid="userUID"
            @request-arrangement="toggleArrangementInterestForCityPost"
            @comment-post="viewPost"
            @delete-post="handleDeleteCityPost"
            @edit-post="handleEditCityPost"
            @report-post="handleReportCityPost"
          />
        </div>
        <div v-else class="empty-posts">
          <p class="empty-sub">No recommendations for {{ city }} yet. Be the first in Explore.</p>
        </div>

        <!-- ── MEMBERS PLANNING TO VISIT ── -->
        <div class="section-hd" style="margin-top: 64px;">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">Traveling Now</span>
          <div class="section-rule"></div>
        </div>
        <div class="posts-header">
          <h2 class="section-title" style="margin-bottom: 0;">Planning a <em>Visit</em></h2>
          <button class="new-post-btn" @click="openCreateExploreDialog('travel_plan')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            <span>I'm Traveling</span>
          </button>
        </div>

        <div v-if="travelPlanPosts.length" class="place-results">
          <TravelPlanCard
            v-for="post in travelPlanPosts"
            :key="post.id"
            :post="post"
            :currentUserUid="userUID"
            @delete-post="handleDeleteCityPost"
            @edit-post="handleEditCityPost"
            @toggle-saved="handleToggleSavedCityPost"
            @report-post="handleReportCityPost"
          />
        </div>
        <div v-else class="empty-posts">
          <p class="empty-sub">No one's declared a trip to {{ city }} yet.</p>
        </div>

        <!-- ── MEMBERS ── -->
        <div class="section-hd" style="margin-top: 64px;">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">The Chapter</span>
          <div class="section-rule"></div>
        </div>
        <h2 class="section-title">Members <em>Here</em></h2>

        <div v-if="chapterMembers.length" class="members-grid">
          <div
            v-for="member in chapterMembers"
            :key="member.id"
            class="member-card"
            @click="$router.push(`/member-detail/${member.id}`)"
          >
            <img
              v-if="member.image"
              :src="member.image"
              :alt="member.fullName"
              class="member-photo"
            />
            <div v-else class="member-photo-placeholder">
              {{ (member.fullName || '?').charAt(0) }}
            </div>
            <div class="member-body">
              <p class="member-name">{{ member.fullName }}</p>
              <p class="member-role">{{ member.title || member.role || 'Member' }}</p>
            </div>
            <div class="member-actions">
              <button class="member-btn" @click.stop="$router.push(`/member-detail/${member.id}`)">
                <div class="btn-tick"></div>
                <span>View Profile</span>
                <div class="btn-tick"></div>
              </button>
            </div>
          </div>
        </div>
        <div v-else class="empty-posts">
          <p class="empty-sub">No members yet — join to be the first.</p>
        </div>

        <!-- ── DISCUSSION ── -->
        <div class="section-hd" style="margin-top: 72px;">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">Talk of the Town</span>
          <div class="section-rule"></div>
        </div>
        <div class="posts-header">
          <h2 class="section-title" style="margin-bottom: 0;">Chapter <em>Discussion</em></h2>
          <button class="new-post-btn" @click="createNewPost">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            <span>New Post</span>
          </button>
        </div>

        <div v-if="posts.length" class="posts-feed">
          <div
            v-for="post in previewPosts"
            :key="post.id"
            class="post-card"
          >
            <!-- Post Header -->
            <div class="post-header">
              <div class="post-avatar">
                <img v-if="post.userProfileImageURL" :src="post.userProfileImageURL" :alt="post.userName" />
                <span v-else>{{ (post.userName || '?').charAt(0) }}</span>
              </div>
              <div class="post-meta">
                <p class="post-author">{{ post.userName }}</p>
                <p class="post-time">{{ formatTimestamp(post.timestamp) }}</p>
              </div>
              <div v-if="post.uid === userUID" class="menu-wrap">
                <button class="menu-btn" @click="openMenuId = openMenuId === post.id ? null : post.id">
                  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="5" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="12" cy="19" r="1.2"/></svg>
                </button>
                <div v-if="openMenuId === post.id" class="menu-dropdown">
                  <button class="menu-item" @click="startEdit(post)">Edit Post</button>
                  <button class="menu-item menu-item-danger" @click="removePost(post)">Delete Post</button>
                </div>
              </div>
              <div v-else class="menu-wrap">
                <button class="menu-btn" @click="openMenuId = openMenuId === post.id ? null : post.id">
                  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="5" r="1.2"/><circle cx="12" cy="12" r="1.2"/><circle cx="12" cy="19" r="1.2"/></svg>
                </button>
                <div v-if="openMenuId === post.id" class="menu-dropdown">
                  <button v-if="!reportedPostIds.includes(post.id)" class="menu-item menu-item-danger" @click="reportingPost = post; openMenuId = null">Report Post</button>
                  <span v-else class="menu-item menu-item-disabled">Reported ✓</span>
                </div>
              </div>
            </div>

            <div class="post-divider"></div>

            <!-- Post Body -->
            <div class="post-body">
              <template v-if="editingPostId === post.id">
                <textarea v-model="editText" class="edit-textarea" rows="3"></textarea>

                <div v-if="editMediaPreviews.length" class="media-grid">
                  <div v-for="(item, idx) in editMediaPreviews" :key="idx" class="media-thumb">
                    <img v-if="item.type === 'image'" :src="item.url" />
                    <video v-else :src="item.url" controls></video>
                    <button class="media-remove-btn" @click="removeEditMediaItem(idx)">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                </div>
                <button class="add-media-btn" @click="$refs['editFileInput' + post.id][0].click()">+ Add Photos/Videos</button>
                <input
                  type="file"
                  :ref="'editFileInput' + post.id"
                  style="display: none;"
                  accept="image/*,video/*"
                  multiple
                  @change="handleEditFileChange"
                />

                <div class="edit-actions">
                  <button class="edit-save-btn" :disabled="!editText.trim() || isSavingEdit" @click="saveEdit(post)">
                    {{ isSavingEdit ? 'Saving…' : 'Save' }}
                  </button>
                  <button class="edit-cancel-btn" @click="cancelEdit">Cancel</button>
                </div>
              </template>
              <template v-else>
                <p class="post-text">{{ post.text }}</p>
                <div v-if="getMedia(post).length" class="media-gallery" :class="{ single: getMedia(post).length === 1 }">
                  <div
                    v-for="(item, idx) in getMedia(post)"
                    :key="idx"
                    class="media-gallery-item"
                    :class="{ clickable: item.type === 'image' }"
                    @click="item.type === 'image' && openLightbox(item)"
                  >
                    <img v-if="item.type === 'image'" :src="item.url" alt="Post image" />
                    <video v-else controls :src="item.url" @click.stop></video>
                  </div>
                </div>
              </template>
            </div>

            <div class="post-divider"></div>

            <!-- Post Actions -->
            <div class="post-actions">
              <button class="post-action-btn" @click="toggleLike(post)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" :class="{ liked: post.likes && post.likes.includes(userUID) }">
                  <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                </svg>
                <span>{{ post.likes ? post.likes.length : 0 }}</span>
              </button>
              <button class="post-action-btn" @click="openComments(post)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
                <span>{{ post.commentsCount || 0 }}</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="posts.length > 3" class="view-all-discussion" @click="viewAllDiscussion">
          <div class="view-all-tick"></div>
          <span>View All Discussion ({{ posts.length }})</span>
          <div class="view-all-tick"></div>
        </div>

        <!-- No posts -->
        <div v-else-if="!posts.length" class="empty-posts">
          <p class="empty-sub">No posts yet. Be the first to talk about {{ city }}.</p>
        </div>

      </template>
    </div>

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
      </button>
      <button class="nav-item" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
      </button>
      <button class="nav-item" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
      </button>
      <button class="nav-item" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
      </button>
      <button class="nav-item active" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
        <div class="nav-dot"></div>
      </button>
      <button class="nav-item" @click="$router.push('/member-settings')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
      </button>
    </nav>

    <!-- ── EDIT CHAPTER MODAL ── -->
    <div v-if="isEditingChapter" class="chapter-edit-overlay" @click.self="cancelEditChapter">
      <div class="chapter-edit-modal">
        <div class="section-hd" style="margin-bottom: 20px;">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">{{ city }}</span>
          <div class="section-rule"></div>
        </div>
        <h2 class="section-title" style="margin-bottom: 20px;">Edit Chapter <em>Details</em></h2>

        <div class="chapter-edit-photo">
          <img v-if="editChapterImagePreview" :src="editChapterImagePreview" alt="Chapter photo" class="chapter-edit-photo-img" />
          <div v-else class="chapter-edit-photo-placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
          </div>
          <button class="chapter-edit-photo-btn" @click="$refs.chapterImageInput.click()">
            {{ editChapterImagePreview ? 'Change Photo' : 'Choose Photo' }}
          </button>
          <input
            type="file"
            ref="chapterImageInput"
            style="display: none;"
            accept="image/*"
            @change="handleChapterImageChange"
          />
        </div>

        <label class="chapter-edit-label">Tagline</label>
        <textarea
          v-model="editChapterDescription"
          class="edit-textarea"
          rows="3"
          placeholder="e.g. The gathering point for members in and around Paris."
        ></textarea>

        <div class="edit-actions">
          <button class="edit-save-btn" :disabled="isSavingChapterEdit" @click="saveChapterEdit">
            {{ isSavingChapterEdit ? 'Saving…' : 'Save' }}
          </button>
          <button class="edit-cancel-btn" @click="cancelEditChapter">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ── EDIT HISTORY ── -->
    <div v-if="showEditHistory" class="chapter-edit-overlay" @click.self="showEditHistory = false">
      <div class="chapter-edit-modal history-modal">
        <div class="section-hd" style="margin-bottom: 20px;">
          <div class="section-rule l"></div>
          <span class="section-eyebrow">{{ city }}</span>
          <div class="section-rule"></div>
        </div>
        <h2 class="section-title" style="margin-bottom: 20px;">Edit <em>History</em></h2>

        <div v-if="isLoadingEditHistory" class="history-empty">Loading…</div>
        <div v-else-if="!editHistoryEntries.length" class="history-empty">No edits recorded yet.</div>
        <div v-else class="history-list">
          <div v-for="entry in editHistoryEntries" :key="entry.id" class="history-row">
            <div class="history-avatar">
              <img v-if="entry.editedByImage" :src="entry.editedByImage" alt="" />
            </div>
            <div class="history-text">
              <p class="history-line">{{ entry.editedByName }} {{ whatChangedText(entry) }}</p>
              <p class="history-time">{{ formatEditTime(entry) }}</p>
            </div>
          </div>
        </div>

        <div class="edit-actions">
          <button class="edit-cancel-btn" style="width: 100%;" @click="showEditHistory = false">Close</button>
        </div>
      </div>
    </div>

    <!-- ── IMAGE LIGHTBOX ── -->
    <div v-if="lightboxMedia" class="lightbox-overlay" @click.self="closeLightbox">
      <button class="lightbox-close" @click="closeLightbox" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <img :src="lightboxMedia.url" alt="Post image" class="lightbox-image" />
    </div>

    <!-- Report reason picker -->
    <div v-if="reportingPost" class="report-overlay" @click.self="reportingPost = null">
      <div class="report-panel">
        <p class="report-title">Report this post</p>
        <button v-for="reason in reportReasons" :key="reason" class="report-reason-btn" @click="submitReport(reason)">
          {{ reason }}
        </button>
        <button class="report-cancel-btn" @click="reportingPost = null">Cancel</button>
      </div>
    </div>

    <!-- ── RECOMMEND A PLACE / I'M TRAVELING (chapter-scoped) ── -->
    <PostCreateDialog
      :model-value="showCreateExploreDialog"
      @update:modelValue="showCreateExploreDialog = $event"
      :user="currentUserProfile"
      :is-public="true"
      :lock-city="city"
      :default-group-only="true"
      :initial-post-type="createExploreInitialType"
      @post-created="onExplorePostCreated"
    />

    <!-- ── EDIT RECOMMENDATION / TRAVEL PLAN (chapter-scoped) ── -->
    <EditPostDialog
      v-if="editingCityPost"
      :model-value="showEditCityPost"
      @update:modelValue="showEditCityPost = $event"
      :post="editingCityPost"
      @post-updated="handlePostUpdatedInChapter"
    />
  </div>
</template>

<script>
import { doc, getDoc, addDoc, updateDoc, deleteDoc, collection, query, where, limit, getDocs, serverTimestamp } from "firebase/firestore";
import { db, auth } from "@/firebase";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { normalizeCity } from "@/utils/city";
import PostCreateDialog from "@/components/PostCreateDialog.vue";
import EditPostDialog from "@/components/EditPostDialog.vue";
import ExplorePostCard from "@/components/ExplorePostCard.vue";
import TravelPlanCard from "@/components/TravelPlanCard.vue";

// Same rule as HomeView.vue / ExploreView.vue / SearchView.vue's Travel
// Intelligence filtering. Kept in sync with their
// TRAVEL_PLAN_AUTO_EXPIRE_DAYS.
const TRAVEL_PLAN_AUTO_EXPIRE_DAYS = 7;

function hasTravelDatePassed(post) {
  const now = new Date();

  if (post.travelEndDate) {
    const end = post.travelEndDate?.toDate
      ? post.travelEndDate.toDate()
      : new Date(post.travelEndDate);
    return now > end;
  }

  const posted = post.timestamp?.toDate
    ? post.timestamp.toDate()
    : (post.timestamp ? new Date(post.timestamp) : null);
  if (!posted) return false;

  const ageMs = now - posted;
  return ageMs > TRAVEL_PLAN_AUTO_EXPIRE_DAYS * 24 * 60 * 60 * 1000;
}

function isTravelPlanExpired(post) {
  // Used for filtering/hiding only — see the identical comment in
  // HomeView.vue / ExploreView.vue / SearchView.vue. This was previously
  // missing the saved check, hiding saved travel plans from the chapter
  // page entirely.
  return hasTravelDatePassed(post) && post.saved !== true;
}

export default {
  name: "GroupDetailView",
  components: { PostCreateDialog, EditPostDialog, ExplorePostCard, TravelPlanCard },
  props: ["id"],

  data() {
    return {
      city: "",
      chapterDoc: null, // { id, city, members: [] } — merged view across all city-name variants, or null
      chapterVariantIds: [], // ids of every `groups` doc that normalizes to this city
      chapterMembers: [],
      cityPosts: [],
      posts: [],
      userUID: null,
      reportingPost: null,
      reportedPostIds: [],
      reportReasons: ['Spam', 'Inappropriate content', 'Harassment', 'False information', 'Other'],
      isLoading: true,
      openMenuId: null,
      editingPostId: null,
      editText: "",
      editMediaFiles: [], // newly-added Files pending upload
      editMediaPreviews: [], // [{ url, type, isNew }] — existing (remote) + newly added (local blob)
      isSavingEdit: false,

      // Edit Chapter Details modal
      isEditingChapter: false,
      editChapterDescription: "",
      editChapterImageFile: null, // newly picked File, or null if unchanged
      editChapterImagePreview: "", // remote URL or local blob preview
      isSavingChapterEdit: false,
      hasEditHistory: false,
      showEditHistory: false,
      editHistoryEntries: [],
      isLoadingEditHistory: false,

      // Image lightbox
      lightboxMedia: null, // { url, type } of the currently viewed image, or null

      // Inline "Recommend a Place" / "I'm Traveling" composer — opens
      // PostCreateDialog in place instead of navigating away to /explore,
      // with the city locked to this chapter and chapter-only (groupOnly)
      // on by default.
      showCreateExploreDialog: false,
      createExploreInitialType: "recommendation",
      currentUserProfile: null, // { uid, fullName, image } — PostCreateDialog needs this shape
      showEditCityPost: false,
      editingCityPost: null,

      nowTick: Date.now(), // ticks periodically so expired travel plans disappear live, not just on next reload
    };
  },

  computed: {
    isMember() {
      return !!(this.chapterDoc && this.userUID && this.chapterDoc.members.includes(this.userUID));
    },

    previewPosts() {
      return this.posts.slice(0, 3);
    },

    recommendationPosts() {
      return this.cityPosts.filter((p) => (p.postType || "recommendation") !== "travel_plan");
    },

    travelPlanPosts() {
      this.nowTick; // eslint-disable-line no-unused-expressions
      return this.cityPosts.filter((p) => p.postType === "travel_plan" && !isTravelPlanExpired(p));
    },

    topCityPosts() {
      return [...this.recommendationPosts]
        .sort((a, b) => (b.arrangementCount || 0) - (a.arrangementCount || 0))
        .slice(0, 5);
    },

    contributorCount() {
      const set = new Set(this.cityPosts.map((p) => p.uid).filter(Boolean));
      if (this.chapterDoc) this.chapterDoc.members.forEach((uid) => set.add(uid));
      return set.size;
    },

    arrangementTotal() {
      return this.recommendationPosts.reduce((sum, p) => sum + (p.arrangementCount || 0), 0);
    },
  },

  async mounted() {
    await this.fetchChapterDetails();
    if (this.chapterDoc && this.chapterDoc.id) {
      this.checkEditHistoryExists();
    }
    this.fetchCurrentUserProfile();
    this._expireTickInterval = setInterval(() => { this.nowTick = Date.now(); }, 15000);
  },

  beforeUnmount() {
    clearInterval(this._expireTickInterval);
  },

  methods: {
    openLightbox(item) {
      this.lightboxMedia = item;
    },

    closeLightbox() {
      this.lightboxMedia = null;
    },

    // Re-fetches just cityPosts, for use after a delete/edit/save/
    // arrangement mutation from ExplorePostCard/TravelPlanCard — cheaper
    // than fetchChapterDetails(), which also re-fetches groups and
    // members every time.
    async refetchCityPosts() {
      const targetKey = normalizeCity(this.city);
      const allExploreSnap = await getDocs(
        query(collection(db, "explore"), where("isPublic", "==", true))
      );
      this.cityPosts = allExploreSnap.docs
        .map((d) => {
          const data = d.data();
          const arrangementInterest = Array.isArray(data.arrangementInterest) ? data.arrangementInterest : [];
          return { id: d.id, ...data, arrangementCount: arrangementInterest.length };
        })
        .filter((p) => p.city && normalizeCity(p.city) === targetKey);
    },

    async fetchChapterDetails() {
      this.isLoading = true;
      try {
        this.city = decodeURIComponent(this.id);
        this.userUID = auth.currentUser?.uid || null;
        const targetKey = normalizeCity(this.city);

        // A city can have more than one `groups` doc if members typed it
        // differently (e.g. "Boston" and "Boston, MA") — merge them so the
        // chapter behaves as one, rather than silently losing data from
        // whichever variant isn't an exact string match.
        const allGroupsSnap = await getDocs(collection(db, "groups"));
        const matchingGroupDocs = allGroupsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((g) => g.city && normalizeCity(g.city) === targetKey);

        this.chapterVariantIds = matchingGroupDocs.map((g) => g.id);

        if (matchingGroupDocs.length === 0) {
          this.chapterDoc = null;
        } else {
          const mergedMembers = [...new Set(matchingGroupDocs.flatMap((g) => g.members || []))];
          // Prefer the variant with the most members as the "primary" doc new
          // writes (join/leave, tagline edits) land on.
          const primary = [...matchingGroupDocs].sort((a, b) => (b.members?.length || 0) - (a.members?.length || 0))[0];
          this.chapterDoc = {
            id: primary.id,
            city: primary.city,
            description: matchingGroupDocs.find((g) => g.description)?.description || primary.description,
            imageURL: matchingGroupDocs.find((g) => g.imageURL)?.imageURL || primary.imageURL,
            members: mergedMembers,
          };
        }

        const allExploreSnap = await getDocs(
          query(collection(db, "explore"), where("isPublic", "==", true))
        );
        this.cityPosts = allExploreSnap.docs
          .map((d) => {
            const data = d.data();
            const arrangementInterest = Array.isArray(data.arrangementInterest) ? data.arrangementInterest : [];
            return { id: d.id, ...data, arrangementCount: arrangementInterest.length };
          })
          .filter((p) => p.city && normalizeCity(p.city) === targetKey);

        await this.fetchChapterMembers();

        if (this.chapterVariantIds.length) {
          const postLists = await Promise.all(
            this.chapterVariantIds.map((groupId) =>
              getDocs(query(collection(db, "posts"), where("groupID", "==", groupId)))
            )
          );
          this.posts = postLists.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } else {
          this.posts = [];
        }
      } catch (error) {
        console.error("Error fetching chapter details:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchChapterMembers() {
      if (!this.chapterDoc || !this.chapterDoc.members.length) {
        this.chapterMembers = [];
        return;
      }
      try {
        const members = await Promise.all(
          this.chapterDoc.members.map(async (memberID) => {
            const memberRef = doc(db, "user", memberID);
            const memberSnap = await getDoc(memberRef);
            return memberSnap.exists() ? { id: memberSnap.id, ...memberSnap.data() } : null;
          })
        );
        this.chapterMembers = members.filter(Boolean);
      } catch (error) {
        console.error("Error fetching chapter members:", error);
      }
    },

    async ensureChapterDoc() {
      if (this.chapterDoc) return this.chapterDoc;
      const docRef = await addDoc(collection(db, "groups"), {
        city: this.city,
        members: [],
        createdAt: serverTimestamp(),
      });
      this.chapterDoc = { id: docRef.id, city: this.city, members: [] };
      this.chapterVariantIds = [docRef.id];
      return this.chapterDoc;
    },

    async toggleMembership() {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      await this.ensureChapterDoc();
      const isCurrentlyMember = this.chapterDoc.members.includes(uid);

      if (isCurrentlyMember) {
        // They may have joined via a different city-name variant's doc than
        // the "primary" one — remove from every variant so leaving actually works.
        await Promise.all(
          this.chapterVariantIds.map(async (groupId) => {
            const snap = await getDoc(doc(db, "groups", groupId));
            if (!snap.exists()) return;
            const members = snap.data().members || [];
            if (members.includes(uid)) {
              await updateDoc(doc(db, "groups", groupId), { members: members.filter((m) => m !== uid) });
            }
          })
        );
      } else {
        await updateDoc(doc(db, "groups", this.chapterDoc.id), { members: [...this.chapterDoc.members, uid] });
      }

      this.userUID = uid;
      await this.fetchChapterDetails();
    },

    async createNewPost() {
      await this.ensureChapterDoc();
      this.$router.push(`/group/${this.chapterDoc.id}/create-post`);
    },

    viewAllDiscussion() {
      this.$router.push(`/groups/${encodeURIComponent(this.city)}/posts`);
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      return new Date(timestamp.seconds * 1000).toLocaleString();
    },

    toggleLike(post) {
      // Add like toggling logic here
    },

    getMedia(post) {
      if (Array.isArray(post.media) && post.media.length) return post.media;
      const legacy = [];
      if (post.imageURL) legacy.push({ url: post.imageURL, type: "image" });
      if (post.videoURL) legacy.push({ url: post.videoURL, type: "video" });
      return legacy;
    },

    startEdit(post) {
      this.openMenuId = null;
      this.editingPostId = post.id;
      this.editText = post.text || "";
      this.editMediaFiles = [];
      // Existing remote media shows immediately; new files get appended as picked
      this.editMediaPreviews = this.getMedia(post).map((item) => ({ ...item, isNew: false }));
    },

    cancelEdit() {
      this.editingPostId = null;
      this.editText = "";
      this.editMediaFiles = [];
      this.editMediaPreviews = [];
    },

    handleEditFileChange(event) {
      const files = Array.from(event.target.files || []);
      files.forEach((file) => {
        this.editMediaFiles.push(file);
        this.editMediaPreviews.push({
          url: URL.createObjectURL(file),
          type: file.type.startsWith("image") ? "image" : "video",
          isNew: true,
        });
      });
      event.target.value = "";
    },

    removeEditMediaItem(idx) {
      const item = this.editMediaPreviews[idx];
      if (item.isNew) {
        // Figure out which pending File this preview corresponds to and drop it too
        const newIdxBefore = this.editMediaPreviews.slice(0, idx).filter((p) => p.isNew).length;
        this.editMediaFiles.splice(newIdxBefore, 1);
      }
      this.editMediaPreviews.splice(idx, 1);
    },

    async saveEdit(post) {
      if (!this.editText.trim() || this.isSavingEdit) return;
      this.isSavingEdit = true;
      try {
        // Upload any newly-added files
        const storage = getStorage();
        const uploaded = await Promise.all(
          this.editMediaFiles.map(async (file, idx) => {
            const isImage = file.type.startsWith("image");
            const ext = isImage ? "jpg" : "mp4";
            const fileRef = storageRef(storage, `chapter_posts/${post.id}_edit_${idx}_${Date.now()}.${ext}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            return { url, type: isImage ? "image" : "video" };
          })
        );

        // Keep existing (non-removed) remote items, in order, plus newly uploaded ones
        const kept = this.editMediaPreviews.filter((p) => !p.isNew).map(({ url, type }) => ({ url, type }));
        const media = [...kept, ...uploaded];

        await updateDoc(doc(db, "posts", post.id), {
          text: this.editText.trim(),
          media,
          imageURL: null, // clear legacy single-media fields now that `media` is authoritative
          videoURL: null,
        });

        post.text = this.editText.trim();
        post.media = media;
        post.imageURL = null;
        post.videoURL = null;

        this.cancelEdit();
      } catch (error) {
        console.error("Error saving post edit:", error);
      } finally {
        this.isSavingEdit = false;
      }
    },

    async removePost(post) {
      this.openMenuId = null;
      if (!window.confirm("Delete this post?")) return;
      try {
        await deleteDoc(doc(db, "posts", post.id));
        this.posts = this.posts.filter((p) => p.id !== post.id);
      } catch (error) {
        console.error("Error deleting post:", error);
      }
    },

    // Same /flags collection and shape as PostsView.vue's submitReport —
    // this file renders its own separate preview copy of the first 3
    // chapter posts (with a "View All Discussion" link to the real feed
    // in PostsView.vue), so it needed the same fix applied twice.
    async submitReport(reason) {
      const post = this.reportingPost;
      this.reportingPost = null;
      if (!post || !auth.currentUser) return;
      try {
        await addDoc(collection(db, "flags"), {
          postId: post.id,
          postType: "chapter",
          chapterId: (this.chapterDoc && this.chapterDoc.id) || null,
          postOwnerUid: post.uid || null,
          reporterUid: auth.currentUser.uid,
          reason,
          status: "pending",
          createdAt: serverTimestamp(),
          postSnapshot: {
            text: post.text || "",
            city: this.city || "",
            image: (this.getMedia(post)[0] && this.getMedia(post)[0].url) || null,
            fullName: post.userName || "",
          },
        });
        this.reportedPostIds.push(post.id);
      } catch (error) {
        console.error("Error submitting report:", error);
      }
    },

    openComments(post) {
      this.$router.push(`/post/${post.id}/comments`);
    },

    viewPost(postID) {
      this.$router.push({ name: "PostDetail", params: { postId: postID } });
    },

    async fetchCurrentUserProfile() {
      const uid = auth.currentUser?.uid;
      console.log("[DEBUG] fetchCurrentUserProfile, uid:", uid);
      if (!uid) {
        console.warn("[DEBUG] no authenticated user — auth.currentUser is null. Dialog will still open, but props.user will be null and submitPost will silently no-op.");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "user", uid));
        const data = snap.exists() ? snap.data() : {};
        this.currentUserProfile = { uid, fullName: data.fullName || "Anonymous", image: data.image || "" };
      } catch (error) {
        console.error("[DEBUG] Error fetching current user profile:", error);
      }
    },

    // Opens PostCreateDialog inline, city locked to this chapter and
    // chapter-only by default — the web equivalent of NewPostSheet.swift's
    // Recommend/Traveling post kinds. Kept alongside openExploreForCity
    // (still a real "browse everything" link) rather than replacing it.
    async openCreateExploreDialog(type) {
      console.log("[DEBUG] openCreateExploreDialog called with type:", type);
      try {
        if (!this.currentUserProfile) {
          console.log("[DEBUG] no currentUserProfile yet, fetching...");
          await this.fetchCurrentUserProfile();
          console.log("[DEBUG] currentUserProfile after fetch:", this.currentUserProfile);
        }
        this.createExploreInitialType = type;
        this.showCreateExploreDialog = true;
        console.log("[DEBUG] showCreateExploreDialog set to:", this.showCreateExploreDialog);
      } catch (err) {
        console.error("[DEBUG] openCreateExploreDialog threw:", err);
      }
    },

    onExplorePostCreated(post) {
      this.showCreateExploreDialog = false;
      const arrangementInterest = Array.isArray(post.arrangementInterest) ? post.arrangementInterest : [];
      const withCount = { ...post, arrangementCount: post.arrangementCount ?? arrangementInterest.length };
      // Insert immediately rather than waiting on a refetch — same
      // reasoning as the iOS optimistic insert: a round trip here is
      // exactly what reads as "it didn't post."
      if (!this.cityPosts.some((p) => p.id === withCount.id)) {
        this.cityPosts.unshift(withCount);
      }
    },

    // The following five mirror ExploreView.vue's handlers of the same
    // shape exactly — same collection, same update logic — since
    // ExplorePostCard/TravelPlanCard emit identically named events
    // regardless of which page hosts them.
    async toggleArrangementInterestForCityPost(postID) {
      const postRef = doc(db, "explore", postID);
      const postSnap = await getDoc(postRef);
      if (!postSnap.exists()) return;
      const postData = postSnap.data();
      const interest = Array.isArray(postData.arrangementInterest) ? postData.arrangementInterest : [];
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const wasRequesting = interest.includes(uid);
      const updated = wasRequesting ? interest.filter((id) => id !== uid) : [...interest, uid];
      await updateDoc(postRef, { arrangementInterest: updated });
      await this.refetchCityPosts();
    },

    async handleDeleteCityPost(postId) {
      if (!window.confirm("Are you sure you want to remove this recommendation?")) return;
      try {
        await deleteDoc(doc(db, "explore", postId));
        await this.refetchCityPosts();
      } catch (err) {
        console.error("Failed to delete post:", err);
      }
    },

    async handleToggleSavedCityPost(post) {
      try {
        await updateDoc(doc(db, "explore", post.id), { saved: !post.saved });
        await this.refetchCityPosts();
      } catch (err) {
        console.error("Failed to toggle saved state:", err);
      }
    },

    handleEditCityPost(post) {
      this.editingCityPost = post;
      this.showEditCityPost = true;
    },

    async handlePostUpdatedInChapter() {
      this.showEditCityPost = false;
      this.editingCityPost = null;
      await this.refetchCityPosts();
    },

    async handleReportCityPost({ post, reason }) {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      try {
        await addDoc(collection(db, "flags"), {
          postId: post.id,
          postType: "explore",
          postOwnerUid: post.uid || null,
          reporterUid: uid,
          reason,
          status: "pending",
          createdAt: serverTimestamp(),
          postSnapshot: {
            text: post.text || "",
            businessName: post.businessName || "",
            city: post.city || "",
            image: post.imageURL || (Array.isArray(post.media) && post.media[0]?.url) || null,
            fullName: post.fullName || "",
          },
        });
      } catch (err) {
        console.error("Failed to submit report:", err);
      }
    },

    openExploreForCity() {
      this.$router.push({ path: "/explore", query: { city: this.city } });
    },

    startEditChapter() {
      this.editChapterDescription = (this.chapterDoc && this.chapterDoc.description) || "";
      this.editChapterImagePreview = (this.chapterDoc && this.chapterDoc.imageURL) || "";
      this.editChapterImageFile = null;
      this.isEditingChapter = true;
    },

    cancelEditChapter() {
      this.isEditingChapter = false;
      this.editChapterImageFile = null;
    },

    handleChapterImageChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.editChapterImageFile = file;
      this.editChapterImagePreview = URL.createObjectURL(file);
      event.target.value = "";
    },

    async saveChapterEdit() {
      if (this.isSavingChapterEdit) return;
      this.isSavingChapterEdit = true;
      try {
        await this.ensureChapterDoc();

        const previousDescription = (this.chapterDoc && this.chapterDoc.description) || "";
        const newDescription = this.editChapterDescription.trim();
        const descriptionChanged = newDescription !== previousDescription;
        const photoChanged = !!this.editChapterImageFile;

        let imageURL = (this.chapterDoc && this.chapterDoc.imageURL) || "";
        if (this.editChapterImageFile) {
          const storage = getStorage();
          const ext = this.editChapterImageFile.name.split(".").pop();
          const fileRef = storageRef(storage, `chapter_images/${this.chapterDoc.id}_${Date.now()}.${ext}`);
          await uploadBytes(fileRef, this.editChapterImageFile);
          imageURL = await getDownloadURL(fileRef);
        }

        await updateDoc(doc(db, "groups", this.chapterDoc.id), {
          description: newDescription,
          imageURL,
        });

        if (descriptionChanged || photoChanged) {
          await this.logChapterEdit(descriptionChanged, photoChanged);
        }

        this.chapterDoc.description = newDescription;
        this.chapterDoc.imageURL = imageURL;
        this.isEditingChapter = false;
      } catch (error) {
        console.error("Error saving chapter details:", error);
      } finally {
        this.isSavingChapterEdit = false;
      }
    },

    // Logs who changed what on this chapter, and when, to
    // groups/{id}/editHistory — previously any edit to a chapter's
    // tagline or photo was completely untracked and untraceable.
    async logChapterEdit(descriptionChanged, photoChanged) {
      try {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        const userSnap = await getDoc(doc(db, "user", uid));
        const userData = userSnap.exists() ? userSnap.data() : {};
        const editorName = userData.fullName || "A member";
        const editorImage = userData.image || null;

        const entry = {
          editedBy: uid,
          editedByName: editorName,
          editedAt: serverTimestamp(),
          changedDescription: descriptionChanged,
          changedPhoto: photoChanged,
        };
        if (editorImage) entry.editedByImage = editorImage;

        await addDoc(collection(db, "groups", this.chapterDoc.id, "editHistory"), entry);
        this.hasEditHistory = true;
      } catch (error) {
        console.error("Error logging chapter edit:", error);
      }
    },

    async checkEditHistoryExists() {
      try {
        const q = query(collection(db, "groups", this.chapterDoc.id, "editHistory"), limit(1));
        const snap = await getDocs(q);
        this.hasEditHistory = !snap.empty;
      } catch (error) {
        console.error("Error checking edit history:", error);
      }
    },

    async openEditHistory() {
      this.showEditHistory = true;
      this.isLoadingEditHistory = true;
      try {
        const snap = await getDocs(collection(db, "groups", this.chapterDoc.id, "editHistory"));
        this.editHistoryEntries = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.editedAt?.toMillis?.() || 0) - (a.editedAt?.toMillis?.() || 0));
      } catch (error) {
        console.error("Error fetching edit history:", error);
        this.editHistoryEntries = [];
      } finally {
        this.isLoadingEditHistory = false;
      }
    },

    whatChangedText(entry) {
      if (entry.changedDescription && entry.changedPhoto) return "updated the tagline and photo";
      if (entry.changedPhoto) return "updated the photo";
      return "updated the tagline";
    },

    formatEditTime(entry) {
      const date = entry.editedAt?.toDate ? entry.editedAt.toDate() : null;
      if (!date) return "Just now";
      const diffMs = Date.now() - date.getTime();
      const diffMins = Math.round(diffMs / 60000);
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.round(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.round(diffHours / 24);
      return `${diffDays}d ago`;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 1100px; margin: 0 auto; padding: 48px 48px 140px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── LOADING / EMPTY ── */
.loading-row { display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 120px 0; }
.loading-seal, .empty-seal { position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 6s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.15); animation: spinSlow 4s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 12px; height: 12px; border: 1px solid rgba(210,175,100,0.6); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.3); }
.loading-text { font-size: 10px; font-weight: 500; letter-spacing: 3px; color: rgba(237,232,223,0.2); text-transform: uppercase; }
.empty-sub { font-size: 11px; font-weight: 400; letter-spacing: 1.5px; color: rgba(237,232,223,0.15); text-transform: uppercase; }

/* ── BACK ── */
.back-btn { display: flex; align-items: center; gap: 8px; background: none; border: none; color: rgba(210,175,100,0.45); font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; cursor: pointer; margin-bottom: 44px; padding: 0; transition: color 0.2s; }
.back-btn:hover { color: rgba(210,175,100,0.8); }
.back-btn svg { width: 16px; height: 16px; }

/* ── HERO ── */
.hero { display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: start; margin-bottom: 16px; }
.hero-img-wrap { position: relative; }
.hero-img { width: 100%; height: 380px; object-fit: cover; object-position: center; display: block; filter: brightness(0.8) saturate(0.7); }
.hero-img-placeholder { width: 100%; height: 380px; background: rgba(210,175,100,0.04); border: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: center; color: rgba(210,175,100,0.25); }
.hero-img-placeholder svg { width: 72px; height: 72px; }
.hero-overlay { position: absolute; inset: 0; background: linear-gradient(135deg, transparent 60%, rgba(7,7,8,0.4)); pointer-events: none; }

.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 16px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 38px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 28px; line-height: 1; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }

.hero-title { font-family: 'Cormorant', serif; font-size: 52px; font-weight: 300; color: #edeae2; letter-spacing: -1px; line-height: 1.05; margin-bottom: 16px; }
.hero-desc { font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.4); line-height: 1.8; letter-spacing: 0.3px; margin-bottom: 32px; max-width: 420px; }

.hero-meta { display: flex; align-items: center; gap: 24px; margin-bottom: 32px; flex-wrap: wrap; }
.meta-chip { display: flex; flex-direction: column; gap: 3px; }
.meta-num { font-family: 'Cormorant', serif; font-size: 32px; font-weight: 300; color: rgba(210,175,100,0.8); line-height: 1; }
.meta-label { font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(237,232,223,0.25); }
.meta-divider { width: 1px; height: 40px; background: rgba(210,175,100,0.15); }

.join-btn { display: flex; align-items: center; gap: 12px; background: rgba(210,175,100,0.08); border: 1px solid rgba(210,175,100,0.5); padding: 13px 24px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,175,100,0.9); transition: background 0.2s, border-color 0.2s, color 0.2s; }
.join-btn:hover { background: rgba(210,175,100,0.16); border-color: rgba(210,175,100,0.75); }
.join-btn.joined { background: transparent; border-color: rgba(237,232,223,0.15); color: rgba(237,232,223,0.35); }
.join-btn.joined:hover { border-color: rgba(200,80,80,0.4); color: rgba(200,80,80,0.7); }
.btn-tick { width: 14px; height: 1px; background: currentColor; opacity: 0.4; flex-shrink: 0; }
.hero-actions { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; }
.edit-details-btn { background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(237,232,223,0.25); transition: color 0.2s; padding: 0; }
.view-history-btn { background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 8.5px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.35); transition: color 0.2s; padding: 0; }
.view-history-btn:hover { color: rgba(210,175,100,0.6); }
.edit-details-btn:hover { color: rgba(210,175,100,0.7); }

/* ── TOP RECOMMENDATIONS (place results) ── */
.place-results { display: flex; flex-direction: column; gap: 16px; max-width: 760px; margin-bottom: 8px; }
.place-result { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); padding: 22px 24px; cursor: pointer; position: relative; transition: border-color 0.2s; }
.place-result::before, .place-result::after { content: ''; position: absolute; width: 12px; height: 12px; }
.place-result::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.35); border-left: 1px solid rgba(210,175,100,0.35); }
.place-result::after { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.35); border-right: 1px solid rgba(210,175,100,0.35); }
.place-result:hover { border-color: rgba(210,175,100,0.3); }
.place-result-tags { margin-bottom: 10px; }
.place-tag { display: inline-flex; align-items: center; gap: 5px; font-size: 8.5px; font-weight: 600; letter-spacing: 1.8px; text-transform: uppercase; padding: 5px 10px; border: 1px solid rgba(210,175,100,0.25); background: rgba(210,175,100,0.08); color: rgba(210,175,100,0.7); }
.place-tag-travel { color: rgba(150,190,220,0.8); border-color: rgba(140,180,210,0.3); background: rgba(140,180,210,0.1); }
.place-result-travel:hover { border-color: rgba(140,180,210,0.4); }
.place-result-name { font-family: 'Cormorant', serif; font-size: 24px; font-weight: 400; color: #edeae2; line-height: 1.15; margin-bottom: 8px; }
.place-result-text { font-size: 13px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.45); margin-bottom: 14px; }
.place-result-media { display: grid; grid-template-columns: repeat(4, 1fr); gap: 3px; margin: 12px 0; }
.place-result-media.single { grid-template-columns: minmax(0, 220px); }
.place-result-media-item { position: relative; aspect-ratio: 1; overflow: hidden; border: 1px solid rgba(210,175,100,0.12); }
.place-result-media-item img, .place-result-media-item video { width: 100%; height: 100%; object-fit: cover; display: block; filter: brightness(0.88) saturate(0.8); }
.place-result-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; padding-top: 12px; border-top: 1px solid rgba(210,175,100,0.07); }
.place-result-author { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(237,232,223,0.25); }
.place-result-arrangement { font-size: 9px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.55); }

/* ── MEMBERS GRID ── */
.members-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.member-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); position: relative; overflow: hidden; cursor: pointer; transition: border-color 0.3s; }
.member-card::before, .member-card::after { content: ''; position: absolute; width: 12px; height: 12px; z-index: 1; }
.member-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.35); border-left: 1px solid rgba(210,175,100,0.35); }
.member-card::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.35); border-right: 1px solid rgba(210,175,100,0.35); }
.member-card:hover { border-color: rgba(210,175,100,0.3); }

.member-photo { width: 100%; height: 180px; object-fit: cover; object-position: top; display: block; filter: brightness(0.88); transition: filter 0.4s; }
.member-card:hover .member-photo { filter: brightness(1); }
.member-photo-placeholder { width: 100%; height: 180px; background: rgba(210,175,100,0.04); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 52px; font-weight: 300; color: rgba(210,175,100,0.2); }

.member-body { padding: 16px 18px 0; }
.member-name { font-family: 'Cormorant', serif; font-size: 20px; font-weight: 400; color: #edeae2; margin-bottom: 4px; line-height: 1.1; }
.member-role { font-size: 9px; font-weight: 500; letter-spacing: 2.5px; color: rgba(210,175,100,0.4); text-transform: uppercase; margin-bottom: 16px; }

.member-actions { display: flex; border-top: 1px solid rgba(210,175,100,0.08); }
.member-btn { flex: 1; background: transparent; border: none; padding: 13px 0; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,175,100,0.5); display: flex; align-items: center; justify-content: center; gap: 8px; position: relative; overflow: hidden; transition: color 0.2s; }
.member-btn::before { content: ''; position: absolute; inset: 0; background: rgba(210,175,100,0.05); transform: scaleX(0); transform-origin: left; transition: transform 0.3s cubic-bezier(0.25,0.46,0.45,0.94); }
.member-card:hover .member-btn::before { transform: scaleX(1); }
.member-btn:hover { color: rgba(210,175,100,0.9); }

/* ── POSTS ── */
.posts-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 32px; flex-wrap: wrap; gap: 12px; }
.new-post-btn { display: flex; align-items: center; gap: 8px; background: transparent; border: 1px solid rgba(210,175,100,0.3); padding: 12px 22px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(210,175,100,0.7); transition: border-color 0.2s, color 0.2s; }
.new-post-btn:hover { border-color: rgba(210,175,100,0.6); color: rgba(210,175,100,1); }
.new-post-btn svg { width: 14px; height: 14px; }

.posts-feed { display: flex; flex-direction: column; gap: 24px; max-width: 680px; margin: 0 auto; }
.post-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); position: relative; }
.post-card::before, .post-card::after { content: ''; position: absolute; width: 12px; height: 12px; z-index: 1; }
.post-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.25); border-left: 1px solid rgba(210,175,100,0.25); }
.post-card::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.25); border-right: 1px solid rgba(210,175,100,0.25); }

.post-header { display: flex; align-items: center; gap: 14px; padding: 18px 22px; }
.post-meta { flex: 1; min-width: 0; }
.post-avatar { width: 40px; height: 40px; border-radius: 50%; overflow: hidden; border: 1px solid rgba(210,175,100,0.2); background: rgba(210,175,100,0.05); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 18px; font-weight: 300; color: rgba(210,175,100,0.5); flex-shrink: 0; }
.post-avatar img { width: 100%; height: 100%; object-fit: cover; }

/* ── OWNER MENU ── */
.menu-wrap { position: relative; flex-shrink: 0; }
.menu-btn { background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.2); padding: 4px; display: flex; align-items: center; transition: color 0.2s; }
.menu-btn:hover { color: rgba(210,175,100,0.6); }
.menu-btn svg { width: 16px; height: 16px; }
.menu-dropdown { position: absolute; top: calc(100% + 6px); right: 0; background: #0f0f10; border: 1px solid rgba(210,175,100,0.2); min-width: 150px; z-index: 50; }
.menu-item { display: block; width: 100%; padding: 12px 16px; background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(237,232,223,0.4); text-align: left; transition: color 0.2s, background 0.2s; }
.menu-item:hover { background: rgba(210,175,100,0.05); color: rgba(237,232,223,0.8); }
.menu-item-danger { color: rgba(180,70,70,0.5); }
.menu-item-danger:hover { color: rgba(200,80,80,0.9); background: rgba(200,80,80,0.04); }
.menu-item-disabled { display: block; padding: 10px 14px; font-size: 12.5px; color: rgba(130,190,140,0.6); }

/* ── REPORT PANEL ── */
.report-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.75); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 24px; }
.report-panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.2); width: 100%; max-width: 340px; padding: 24px; }
.report-title { font-family: 'Cormorant', serif; font-size: 19px; color: #edeae2; margin-bottom: 16px; }
.report-reason-btn { display: block; width: 100%; text-align: left; background: rgba(210,175,100,0.03); border: 1px solid rgba(210,175,100,0.1); color: rgba(237,232,223,0.6); font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 300; padding: 12px 14px; margin-bottom: 8px; cursor: pointer; }
.report-reason-btn:hover { border-color: rgba(200,80,80,0.35); color: rgba(200,80,80,0.85); }
.report-cancel-btn { display: block; width: 100%; text-align: center; background: none; border: none; color: rgba(237,232,223,0.3); font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; padding: 10px; margin-top: 6px; cursor: pointer; }

/* ── INLINE EDIT ── */
.edit-textarea { width: 100%; background: rgba(210,175,100,0.03); border: 1px solid rgba(210,175,100,0.25); padding: 12px; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); resize: vertical; margin-bottom: 12px; }
.edit-textarea:focus { border-color: rgba(210,175,100,0.5); }
.edit-actions { display: flex; gap: 10px; }
.edit-save-btn { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(210,175,100,0.9); background: rgba(210,175,100,0.08); border: 1px solid rgba(210,175,100,0.5); padding: 9px 18px; cursor: pointer; transition: background 0.2s, opacity 0.2s; }
.edit-save-btn:hover:not(:disabled) { background: rgba(210,175,100,0.16); }
.edit-save-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.edit-cancel-btn { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: rgba(237,232,223,0.3); background: none; border: none; cursor: pointer; padding: 9px 6px; transition: color 0.2s; }
.edit-cancel-btn:hover { color: rgba(237,232,223,0.6); }

.chapter-edit-overlay { position: fixed; inset: 0; z-index: 50; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; padding: 20px; }
.chapter-edit-modal { width: 100%; max-width: 420px; max-height: 88vh; overflow-y: auto; background: #0e0e0f; border: 1px solid rgba(210,175,100,0.25); padding: 28px; }

.history-empty { font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.35); padding: 24px 0; text-align: center; }
.history-list { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
.history-row { display: flex; align-items: center; gap: 12px; }
.history-avatar { width: 36px; height: 36px; border-radius: 50%; background: rgba(210,175,100,0.15); flex-shrink: 0; overflow: hidden; }
.history-avatar img { width: 100%; height: 100%; object-fit: cover; }
.history-line { font-size: 13px; font-weight: 300; color: #edeae2; margin: 0; }
.history-time { font-size: 10px; font-weight: 300; color: rgba(237,232,223,0.25); margin: 2px 0 0; }
.chapter-edit-photo { display: flex; flex-direction: column; align-items: center; gap: 12px; margin-bottom: 24px; }
.chapter-edit-photo-img { width: 100%; height: 140px; object-fit: cover; filter: brightness(0.85) saturate(0.8); }
.chapter-edit-photo-placeholder { width: 100%; height: 100px; display: flex; align-items: center; justify-content: center; background: rgba(210,175,100,0.05); border: 1px solid rgba(210,175,100,0.15); }
.chapter-edit-photo-placeholder svg { width: 26px; height: 26px; color: rgba(210,175,100,0.25); }
.chapter-edit-photo-btn { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,175,100,0.55); background: none; border: 1px solid rgba(210,175,100,0.2); cursor: pointer; padding: 10px 16px; width: 100%; transition: color 0.2s, border-color 0.2s; }
.chapter-edit-photo-btn:hover { color: rgba(210,175,100,0.85); border-color: rgba(210,175,100,0.4); }
.chapter-edit-label { display: block; font-family: 'Montserrat', sans-serif; font-size: 8px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase; color: rgba(210,175,100,0.4); margin-bottom: 8px; }
.post-author { font-family: 'Cormorant', serif; font-size: 18px; font-weight: 400; color: #edeae2; margin-bottom: 2px; }
.post-time { font-size: 9px; font-weight: 500; letter-spacing: 2px; color: rgba(237,232,223,0.2); text-transform: uppercase; }
.post-divider { height: 1px; background: rgba(210,175,100,0.08); }

.post-body { padding: 20px 22px; }
.post-text { font-size: 14px; font-weight: 300; color: rgba(237,232,223,0.7); line-height: 1.8; letter-spacing: 0.2px; margin-bottom: 16px; }
.post-text:last-child { margin-bottom: 0; }
.media-gallery { display: grid; grid-template-columns: repeat(2, 1fr); gap: 3px; margin-top: 8px; }
.media-gallery.single { grid-template-columns: 1fr; }
.media-gallery-item { position: relative; aspect-ratio: 1; overflow: hidden; }
.media-gallery.single .media-gallery-item { aspect-ratio: 16/10; }
.media-gallery-item img, .media-gallery-item video { width: 100%; height: 100%; object-fit: cover; display: block; filter: brightness(0.88) saturate(0.8); }
.media-gallery-item.clickable { cursor: pointer; }
.media-gallery-item.clickable:hover img { filter: brightness(1) saturate(1); }

.lightbox-overlay { position: fixed; inset: 0; z-index: 80; background: rgba(0,0,0,0.92); display: flex; align-items: center; justify-content: center; padding: 32px; }
.lightbox-image { max-width: 100%; max-height: 100%; object-fit: contain; }
.lightbox-close { position: absolute; top: 20px; right: 20px; width: 38px; height: 38px; display: flex; align-items: center; justify-content: center; background: rgba(210,175,100,0.08); border: 1px solid rgba(210,175,100,0.25); color: rgba(237,232,223,0.7); cursor: pointer; transition: background 0.2s, color 0.2s; }
.lightbox-close:hover { background: rgba(210,175,100,0.16); color: rgba(237,232,223,1); }
.lightbox-close svg { width: 16px; height: 16px; }

.media-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
.media-thumb { position: relative; aspect-ratio: 1; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; }
.media-thumb img, .media-thumb video { width: 100%; height: 100%; object-fit: cover; display: block; }
.media-remove-btn { position: absolute; top: 4px; right: 4px; width: 20px; height: 20px; border-radius: 50%; background: rgba(0,0,0,0.7); border: 1px solid rgba(210,175,100,0.3); color: rgba(237,232,223,0.8); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s, color 0.2s; }
.media-remove-btn:hover { background: rgba(200,60,60,0.7); color: #fff; }
.media-remove-btn svg { width: 11px; height: 11px; }
.add-media-btn { display: inline-block; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.6); background: transparent; border: 1px solid rgba(210,175,100,0.25); padding: 8px 14px; cursor: pointer; margin-bottom: 12px; transition: border-color 0.2s, color 0.2s; }
.add-media-btn:hover { border-color: rgba(210,175,100,0.55); color: rgba(210,175,100,0.9); }

.post-actions { display: flex; align-items: center; gap: 0; }
.post-action-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px; background: transparent; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 1px; color: rgba(237,232,223,0.2); transition: color 0.2s; }
.post-action-btn:hover { color: rgba(210,175,100,0.6); }
.post-action-btn + .post-action-btn { border-left: 1px solid rgba(210,175,100,0.08); }
.post-action-btn svg { width: 16px; height: 16px; transition: stroke 0.2s; }
.post-action-btn svg.liked { stroke: rgba(200,80,80,0.8); fill: rgba(200,80,80,0.2); }

.empty-posts { padding: 40px 0; text-align: center; border: 1px solid rgba(210,175,100,0.06); margin-bottom: 8px; }
.view-all-discussion { display: flex; align-items: center; justify-content: center; gap: 14px; margin-top: 20px; padding: 16px 0; border: 1px solid rgba(210,175,100,0.18); font-size: 9px; font-weight: 600; letter-spacing: 3px; color: rgba(210,175,100,0.7); text-transform: uppercase; cursor: pointer; transition: border-color 0.2s, color 0.2s; max-width: 680px; margin-left: auto; margin-right: auto; }
.view-all-discussion:hover { border-color: rgba(210,175,100,0.5); color: rgba(210,175,100,1); }
.view-all-tick { width: 16px; height: 1px; background: rgba(210,175,100,0.3); flex-shrink: 0; }

/* ── BOTTOM NAV ── */
.bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 20; background: #070708; border-top: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: space-around; padding: 14px 0 18px; }
.bottom-nav-glow { position: absolute; top: -1px; left: 10%; right: 10%; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent); pointer-events: none; }
.nav-item { display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; padding: 4px 20px; color: rgba(237,232,223,0.2); transition: color 0.2s; background: none; border: none; font-family: 'Montserrat', sans-serif; min-width: 60px; }
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
.nav-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.7); margin-top: 1px; }

/* ── RESPONSIVE ── */
@media (max-width: 900px) { .hero { grid-template-columns: 1fr; gap: 32px; } .members-grid { grid-template-columns: repeat(2, 1fr); } .page { padding: 40px 28px 120px; } }
@media (max-width: 600px) { .members-grid { grid-template-columns: 1fr; } .page { padding: 32px 20px 120px; } .frame, .frame-inner { inset: 12px; } .hero-title { font-size: 36px; } .section-title { font-size: 30px; } .posts-header { flex-direction: column; align-items: flex-start; gap: 16px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>